import { OperationRoutes } from "@/shared/common/constants";
import { OperationCommand } from "@/shared/common/enums";
import type { IMenuHandlerProps } from "@/shared/common/types";
import { buildRoute } from "@/shared/helpers";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Props {
  filters?: string[];
  id?: string;
  type?: string;
}

interface IRedirectActionProps {
  operationId: string;
  operationType?: string;
  actionType: string;
}

export const useOperationActions = ({
  filters,
  id,
  type,
}: Props = {}) => {
  const { t, i18n: { isInitializing } } = useTranslation();
  const navigate = useNavigate();

  const [isOpenSendToEdit, setIsOpenSendToEdit] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const redirectToPageAction = ({
    operationId,
    operationType,
    actionType,
  }: IRedirectActionProps) => {
    if (!operationId && !id) return;
    const path = OperationRoutes?.[`${operationType || type}${actionType}` as keyof typeof OperationRoutes];

    const url = buildRoute(path, { operationId: `${operationId || id}` });
    navigate(url);
  };

  const actions = [
    {
      label: t("general.copy"),
      value: OperationCommand.Copy,
      handler: (props?: IMenuHandlerProps) => {
        redirectToPageAction({
          operationId: `${props?.operationId || id}`,
          operationType: props?.operationType || type,
          actionType: "Copy",
        });
      },
    },
    {
      label: t("general.edit"),
      value: OperationCommand.Edit,
      handler: (props?: IMenuHandlerProps) => {
        redirectToPageAction({
          operationId: `${props?.operationId || id}`,
          operationType: props?.operationType || type,
          actionType: "Update",
        });
      },
    },
    {
      label: t("general.sendToEdit"),
      value: OperationCommand.SendToEdit,
      handler: () => {
        setIsOpenSendToEdit(true);
      },
    },
    {
      label: t("general.delete"),
      value: OperationCommand.Delete,
      handler: () => {
        setIsDeleteModalOpen(true);
      },
    },
  ];

  const filteredActions = useMemo(() => {
    return filters ?
      actions.filter((action) => filters?.includes(action.value)) :
      actions;
  }, [filters, isInitializing]);

  return {
    actions: filteredActions,
    isOpenSendToEdit,
    setIsOpenSendToEdit,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
  };
};
